import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
	AudioChannelFeatureUpdateInput,
	NodeUpdateInput,
	PassiveSpeakerFeatureUpdateInput
} from '@shure/shared/angular/data-access/system-api/core';
import { handleMutationResult } from '@shure/shared/angular/data-access/system-api/mappers';
import { ILogger } from '@shure/shared/angular/utils/logging';

import { AudioChannelCoreApiService } from '../api';

import {
	AudioChannelStartIdentifyMutationGQL,
	AudioChannelStopIdentifyMutationGQL,
	UpdateAudioChannelMutationGQL,
	UpdatePassiveSpeakerMutationGQL
} from './graphql/generated/audio-channel-core.generated';

@Injectable()
export class SysApiAudioChannelCoreApiService implements AudioChannelCoreApiService {
	private readonly logger: ILogger;

	constructor(
		logger: ILogger,
		private readonly updateAudioChannelMutationGQL: UpdateAudioChannelMutationGQL,
		private readonly audioChannelStartIdentifyMutationGql: AudioChannelStartIdentifyMutationGQL,
		private readonly audioChannelStopIdentifyMutationGql: AudioChannelStopIdentifyMutationGQL,
		private readonly updatePassiveSpeakerMutationGQL: UpdatePassiveSpeakerMutationGQL
	) {
		this.logger = logger.createScopedLogger('SysApiAudioChannelCoreApiService');
	}

	public updateAudioChannel(channelId: string, inputs: AudioChannelFeatureUpdateInput[]): Observable<void> {
		const updates = inputs.reduce<NodeUpdateInput[]>((all, next, i) => {
			all[i] = { audioChannel: { id: channelId, features: next } };
			return all;
		}, new Array(inputs.length));

		return this.updateAudioChannelMutationGQL
			.mutate({ variables: { updates, injectKeyring: '//supplied-by-keyring-injector' } })
			.pipe(handleMutationResult('updateNodes', this.logger, 'updateAudioChannel()', { channelId, updates }));
	}

	public updateAudioChannels(
		channelInputs: { channelId: string; inputs: AudioChannelFeatureUpdateInput[] }[]
	): Observable<void> {
		const updates = channelInputs.reduce<NodeUpdateInput[]>((all, { channelId, inputs }) => {
			all.push(...inputs.map((input) => ({ audioChannel: { id: channelId, features: input } })));
			return all;
		}, []);

		return this.updateAudioChannelMutationGQL
			.mutate({ variables: { updates, injectKeyring: '//supplied-by-keyring-injector' } })
			.pipe(handleMutationResult('updateNodes', this.logger, 'updateAudioChannels()', { updates }));
	}

	public updateAudioPassiveSpeaker(
		passiveSpeakerId: string,
		inputs: PassiveSpeakerFeatureUpdateInput[]
	): Observable<void> {
		return this.updateAudioPassiveSpeakers([{ speakerId: passiveSpeakerId, inputs }]);
	}

	public updateAudioPassiveSpeakers(
		passiveSpeakerInputs: { speakerId: string; inputs: PassiveSpeakerFeatureUpdateInput[] }[]
	): Observable<void> {
		const updates = passiveSpeakerInputs.reduce<NodeUpdateInput[]>((all, { speakerId, inputs }) => {
			all.push(...inputs.map((input) => ({ passiveSpeaker: { id: speakerId, features: input } })));
			return all;
		}, []);

		return this.updatePassiveSpeakerMutationGQL
			.mutate({ variables: { updates, injectKeyring: '//supplied-by-keyring-injector' } })
			.pipe(handleMutationResult('updateNodes', this.logger, 'updateAudioPassiveSpeakers()', { updates }));
	}
	public setAudioChannelIdentify(channelId: string, identifying: boolean): Observable<void> {
		return identifying ? this.startAudioChannelIdentify(channelId) : this.stopAudioChannelIdentify(channelId);
	}

	private startAudioChannelIdentify(channelId: string): Observable<void> {
		return this.audioChannelStartIdentifyMutationGql
			.mutate({ variables: { startAudioChannelIdentifyId: channelId } })
			.pipe(
				handleMutationResult('startAudioChannelIdentify', this.logger, 'startAudioChannelIdentify()', {
					channelId
				})
			);
	}

	private stopAudioChannelIdentify(channelId: string): Observable<void> {
		return this.audioChannelStopIdentifyMutationGql
			.mutate({ variables: { stopAudioChannelIdentifyId: channelId } })
			.pipe(
				handleMutationResult('stopAudioChannelIdentify', this.logger, 'stopAudioChannelIdentify()', {
					channelId
				})
			);
	}
}
